import React, { useEffect, useState } from "react";
import CardWrapper from "../shared/CardWrapper/CardWrapper";
import Tabs from "../shared/Tabs";
import BarChart from "../shared/BarChart/BarChart";
import { generateClient } from "aws-amplify/api";
import {
  getWaterFlowDistributionX04Query,
  getWaterFlowProductionX08Query,
  waterFlowComparisonQuery,
  waterProductionDistributionHistory,
} from "../util/query";
import GraphSkeleton from "../util/graphSkeleton";
import { divideArray, getTimeFormattedArray } from "../util/helper";

const PERIOD_OPTIONS = ["Day", "Week", "Month", "Year"];
const DAY_MS = 24 * 60 * 60 * 1000;
const BUCKET_MS_BY_PERIOD = {
  Day: 10 * 60 * 1000,
  Week: DAY_MS,
  Month: DAY_MS,
};
const MS_PER_MINUTE = 60 * 1000;

const ChartIcon = ({ children }) => (
  <svg
    aria-hidden="true"
    className="dashboard-icon"
    fill="none"
    focusable="false"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    {children}
  </svg>
);

const ChevronLeftIcon = () => (
  <ChartIcon>
    <path d="m15 18-6-6 6-6" />
  </ChartIcon>
);

const ChevronRightIcon = () => (
  <ChartIcon>
    <path d="m9 18 6-6-6-6" />
  </ChartIcon>
);

const getUtcToday = () => {
  const now = new Date();
  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
};

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const getPeriodUnit = (period) => String(period || "period").toLowerCase();

const getComparableDate = (startMs, period, slot) => {
  const startDate = new Date(startMs);
  if (period === "Year") {
    return new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth() + slot,
        1
      )
    );
  }
  if (period === "Month") {
    return new Date(startMs + slot * DAY_MS);
  }
  if (period === "Week") {
    return new Date(startMs + slot * 7 * 60 * MS_PER_MINUTE);
  }
  return new Date(startMs + slot * (BUCKET_MS_BY_PERIOD[period] || DAY_MS));
};

const formatComparableLabel = (period, date) => {
  if (period === "Day") {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  if (period === "Week") {
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  if (period === "Year") {
    return date.toLocaleString(undefined, { month: "short" });
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const getMonthSlot = (bucketMs, startMs) => {
  const bucketDate = new Date(bucketMs);
  const startDate = new Date(startMs);
  const bucketDay = Date.UTC(
    bucketDate.getUTCFullYear(),
    bucketDate.getUTCMonth(),
    bucketDate.getUTCDate()
  );
  const startDay = Date.UTC(
    startDate.getUTCFullYear(),
    startDate.getUTCMonth(),
    startDate.getUTCDate()
  );
  return Math.round((bucketDay - startDay) / DAY_MS);
};

const getYearSlot = (bucketMs, startMs) => {
  const bucketDate = new Date(bucketMs);
  const startDate = new Date(startMs);
  return (
    (bucketDate.getUTCFullYear() - startDate.getUTCFullYear()) * 12 +
    bucketDate.getUTCMonth() -
    startDate.getUTCMonth()
  );
};

const getBucketSlot = (bucket, startMs, period) => {
  const bucketMs = Date.parse(bucket?.time_interval);
  if (!Number.isFinite(bucketMs) || !Number.isFinite(startMs)) {
    return null;
  }

  if (period === "Year") {
    return Math.max(0, getYearSlot(bucketMs, startMs));
  }

  if (period === "Month") {
    return Math.max(0, getMonthSlot(bucketMs, startMs));
  }

  if (period === "Week") {
    return Math.max(0, Math.round((bucketMs - startMs) / (7 * 60 * MS_PER_MINUTE)));
  }

  const bucketSize = BUCKET_MS_BY_PERIOD[period] || BUCKET_MS_BY_PERIOD.Day;
  return Math.max(0, Math.round((bucketMs - startMs) / bucketSize));
};

const getBucketValue = (bucket) => {
  const rawValue = bucket?.Flow_Lpmin;
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return null;
  }

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : null;
};

const buildBucketMap = (buckets, startMs, period) => {
  const map = new Map();
  (buckets || []).forEach((bucket) => {
    const slot = getBucketSlot(bucket, startMs, period);
    const value = getBucketValue(bucket);
    if (slot !== null && value !== null) {
      map.set(slot, value);
    }
  });
  return map;
};

const getWeekStart = (date) => {
  const day = date.getUTCDay();
  const mondayOffset = day === 0 ? 6 : day - 1;
  const start = new Date(date);
  start.setUTCDate(start.getUTCDate() - mondayOffset);
  return start;
};

const addUtcMonths = (date, monthDelta) =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + monthDelta, 1)
  );

const getPeriodStartFromOffset = (period, offset) => {
  const safeOffset = Math.max(0, Number(offset) || 0);
  const today = getUtcToday();

  if (period === "Week") {
    const weekStart = getWeekStart(today);
    weekStart.setUTCDate(weekStart.getUTCDate() - safeOffset * 7);
    return weekStart;
  }

  if (period === "Month") {
    return addUtcMonths(
      new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1)),
      -safeOffset
    );
  }

  if (period === "Year") {
    return new Date(Date.UTC(today.getUTCFullYear() - safeOffset, 0, 1));
  }

  const start = new Date(today);
  start.setUTCDate(start.getUTCDate() - safeOffset);
  return start;
};

const formatShortDate = (date, options) =>
  date.toLocaleDateString(undefined, {
    timeZone: "UTC",
    ...options,
  });

const formatDateLabel = (date) =>
  formatShortDate(date, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

const getPeriodDisplayLabel = (period, offset) => {
  const safeOffset = Math.max(0, Number(offset) || 0);
  const today = getUtcToday();

  if (period === "Day") {
    const day = getPeriodStartFromOffset(period, safeOffset);
    return formatDateLabel(day);
  }

  if (period === "Week") {
    const windowStart = getPeriodStartFromOffset(period, safeOffset);
    const windowEnd = new Date(windowStart);
    windowEnd.setUTCDate(windowEnd.getUTCDate() + 6);
    return `${formatDateLabel(windowStart)} - ${formatDateLabel(windowEnd)}`;
  }

  if (period === "Month") {
    const selectedMonthStart = getPeriodStartFromOffset(period, safeOffset);
    return formatShortDate(selectedMonthStart, {
      month: "short",
      year: "numeric",
    });
  }

  if (period === "Year") {
    const selectedYear = today.getUTCFullYear() - safeOffset;
    return `${selectedYear}`;
  }

  return formatDateLabel(today);
};

const getOffsetFromDate = (period, inputValue) => {
  if (!inputValue) {
    return 0;
  }

  const selectedDate = new Date(`${inputValue}T00:00:00Z`);
  if (Number.isNaN(selectedDate.getTime())) {
    return 0;
  }

  const today = getUtcToday();

  if (period === "Week") {
    const currentWeek = getWeekStart(today);
    const selectedWeek = getWeekStart(selectedDate);
    return Math.max(0, Math.floor((currentWeek - selectedWeek) / (DAY_MS * 7)));
  }

  if (period === "Month") {
    return Math.max(
      0,
      (today.getUTCFullYear() - selectedDate.getUTCFullYear()) * 12 +
        today.getUTCMonth() -
        selectedDate.getUTCMonth()
    );
  }

  if (period === "Year") {
    return Math.max(0, today.getUTCFullYear() - selectedDate.getUTCFullYear());
  }

  return Math.max(0, Math.floor((today - selectedDate) / DAY_MS));
};

const getPeriodInputValue = (period, offset) => {
  const dateValue = toDateInputValue(getPeriodStartFromOffset(period, offset));
  if (period === "Month") {
    return dateValue.slice(0, 7);
  }
  if (period === "Year") {
    return dateValue.slice(0, 4);
  }
  return dateValue;
};

const getPeriodInputType = (period) => {
  if (period === "Month") {
    return "month";
  }
  if (period === "Year") {
    return "text";
  }
  return "date";
};

const getPeriodInputPlaceholder = (period) => {
  if (period === "Month") {
    return "YYYY-MM";
  }
  if (period === "Year") {
    return "YYYY";
  }
  return "YYYY-MM-DD";
};

const getOffsetFromPeriodInput = (period, inputValue) => {
  const value = String(inputValue || "").trim();
  if (!value) {
    return 1;
  }

  if (period === "Month" && /^\d{4}-\d{2}$/.test(value)) {
    return getOffsetFromDate(period, `${value}-01`);
  }

  if (period === "Year" && /^\d{4}$/.test(value)) {
    return getOffsetFromDate(period, `${value}-01-01`);
  }

  return getOffsetFromDate(period, value);
};

function Bar({ setSelectedFilter, setTab, plantId }) {
  const [activeTab, setActiveTab] = useState("tab1");
  const [selected, setSelected] = useState("Day");
  const [periodOffset, setPeriodOffset] = useState(0);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonMode, setComparisonMode] = useState("previous");
  const [customComparisonOffset, setCustomComparisonOffset] = useState(1);
  const [customComparisonValue, setCustomComparisonValue] = useState("");
  const [zoomLevel, setZoomLevel] = useState(0);
  const [flowIn, setFlowIn] = useState();
  const [flowOut, setFlowOut] = useState();
  const [flowInComparison, setFlowInComparison] = useState();
  const [flowOutComparison, setFlowOutComparison] = useState();
  const [flowStats, setFlowStats] = useState();
  const [refinedLabel, setRefinedLabel] = useState();

  const tabs = [
    { id: "tab1", title: "Produced", content: "Produced" },
    { id: "tab2", title: "Distributed", content: "Distributed" },
  ];

  const client = generateClient();
  const hasPlantId =
    typeof plantId === "string" &&
    plantId.trim() !== "" &&
    plantId !== "undefined" &&
    plantId !== "null";

  const selectedDateValue = toDateInputValue(
    getPeriodStartFromOffset(selected, periodOffset)
  );
  const comparisonOptions = [
    { value: "off", label: "Off" },
    { value: "previous", label: "Previous" },
    { value: "previousYear", label: "Last year" },
    { value: "custom", label: "Custom" },
  ];

  useEffect(() => {
    setCustomComparisonValue(
      getPeriodInputValue(selected, customComparisonOffset)
    );
  }, [selected, customComparisonOffset]);

  const getHistoryLimit = () => {
    const baseLimitByPeriod = {
      Day: 300,
      Week: 1200,
      Month: 5000,
      Year: 20000,
    };
    return baseLimitByPeriod[selected] || 300;
  };

  const resetChartData = () => {
    setFlowIn();
    setFlowOut();
    setFlowInComparison();
    setFlowOutComparison();
    setRefinedLabel();
  };

  const resetFlowStats = () => {
    setFlowStats();
  };

  const getComparisonValue = () =>
    comparisonMode === "custom"
      ? "custom"
      : comparisonMode === "previousYear"
      ? "previousYear"
      : "previous";

  const getComparisonOffset = () =>
    comparisonMode === "custom" ? customComparisonOffset : undefined;

  const buildComparisonChart = (result) => {
    const currentBuckets = result?.currentBuckets || [];
    const comparisonBuckets = result?.comparisonBuckets || [];
    const currentStartMs = Date.parse(result?.currentStart);
    const comparisonStartMs = Date.parse(result?.comparisonStart);
    const currentEndMs = Date.parse(result?.currentEnd);
    const comparisonEndMs = Date.parse(result?.comparisonEnd);
    const currentMap = buildBucketMap(
      currentBuckets,
      currentStartMs,
      selected,
      currentEndMs
    );
    const comparisonMap = buildBucketMap(
      comparisonBuckets,
      comparisonStartMs,
      selected,
      comparisonEndMs
    );
    const slotSet = comparisonEnabled
      ? new Set([
          ...Array.from(currentMap.keys()),
          ...Array.from(comparisonMap.keys()),
        ])
      : new Set(Array.from(currentMap.keys()));
    const slots = Array.from(slotSet).sort((a, b) => a - b);
    const currentValues = slots.map((slot) =>
      currentMap.has(slot) ? currentMap.get(slot) : null
    );
    const comparisonValues = slots.map((slot) =>
      comparisonMap.has(slot) ? comparisonMap.get(slot) : null
    );
    const labels = slots.map((slot) =>
      formatComparableLabel(
        selected,
        getComparableDate(currentStartMs, selected, slot)
      )
    );
    const selectedLegendLabel = result?.currentLabel
      ? `This period: ${result.currentLabel}`
      : "This period";
    const comparedLegendLabel = result?.comparisonLabel
      ? `Compared: ${result.comparisonLabel}`
      : "Compared";
    const chartUnit = "l/min";
    const series = [
      {
        label: selectedLegendLabel,
        shortLabel: "This period",
        data: currentValues,
        color: "rgba(35, 203, 216, 0.96)",
        unit: chartUnit,
      },
    ];

    if (comparisonEnabled) {
      series.push({
        label: comparedLegendLabel,
        shortLabel: "Compared",
        data: comparisonValues,
        color: "rgba(231, 195, 90, 0.86)",
        unit: chartUnit,
      });
    }

    return {
      barData: {
        data: divideArray(currentValues, 1),
        interval: "minute",
        unit: chartUnit,
      },
      labels,
      series,
    };
  };

  async function fetchFallbackHistory(port, setFlow, setComparison) {
    if (!hasPlantId) {
      setFlow({ data: [], interval: "minute" });
      setComparison(null);
      setRefinedLabel([]);
      return;
    }
    try {
      const query = waterProductionDistributionHistory(
        selected.toLowerCase(),
        port,
        plantId,
        getHistoryLimit()
      );
      const response = await client.graphql({ query });
      if (response) {
        const records =
          response.data.getHistoryWaterFlowx04Andx08ProductionAndDistribution ||
          [];
        const filteredData = records.map((item) =>
          Number(Number(item.Flow_Lpmin) || 0).toFixed(2)
        );

        const refinedData = divideArray([...filteredData].reverse(), 1);
        setFlow({ data: refinedData, interval: "minute" });
        setComparison(null);
        setRefinedLabel(getTimeFormattedArray([...records].reverse(), 1));
      }
    } catch (error) {
      console.error("Error fetching flow history data:", error);
    }
  }

  async function fetchFlowComparison(port, setFlow, setComparison) {
    if (!hasPlantId) {
      setFlow({ data: [], interval: "minute" });
      setComparison(null);
      setRefinedLabel([]);
      return;
    }
    try {
      const query = waterFlowComparisonQuery(
        selected.toLowerCase(),
        port,
        plantId,
        periodOffset,
        getComparisonValue(),
        getComparisonOffset()
      );
      const response = await client.graphql({ query });
      const result = response?.data?.getWaterFlowComparison;
      if (result) {
        const chart = buildComparisonChart(result);
        setFlow(chart.barData);
        setComparison({ ...result, series: chart.series });
        setRefinedLabel(chart.labels);
      } else {
        await fetchFallbackHistory(port, setFlow, setComparison);
      }
    } catch (error) {
      console.error("Error fetching flow comparison data:", error);
      await fetchFallbackHistory(port, setFlow, setComparison);
    }
  }

  async function fetchFlowStats() {
    if (!hasPlantId) {
      setFlowStats(null);
      return;
    }

    try {
      const query =
        activeTab === "tab1"
          ? getWaterFlowProductionX08Query("24h", plantId)
          : getWaterFlowDistributionX04Query("24h", plantId);
      const response = await client.graphql({ query });
      const payload =
        activeTab === "tab1"
          ? response?.data?.getWaterFlowProductionX08
          : response?.data?.getWaterFlowDistributionX04;
      setFlowStats(payload || null);
    } catch (error) {
      console.error("Error fetching flow summary data:", error);
      setFlowStats(null);
    }
  }

  useEffect(() => {
    const fetchData = () => {
      if (activeTab === "tab1") {
        fetchFlowComparison("x08", setFlowIn, setFlowInComparison);
      } else {
        fetchFlowComparison("x04", setFlowOut, setFlowOutComparison);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 600000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    selected,
    periodOffset,
    comparisonMode,
    customComparisonOffset,
    plantId,
    comparisonEnabled,
  ]);

  useEffect(() => {
    fetchFlowStats();
    const interval = setInterval(fetchFlowStats, 600000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, plantId]);

  const activeFlow = activeTab === "tab1" ? flowIn : flowOut;
  const activeComparison =
    activeTab === "tab1" ? flowInComparison : flowOutComparison;
  const activeSeries = activeComparison?.series;
  const periodDisplayLabel =
    activeComparison?.currentLabel ||
    getPeriodDisplayLabel(selected, periodOffset);
  const currentFlowValue =
    activeTab === "tab1"
      ? flowStats?.current_flowLpmin_x08
      : flowStats?.current_flowLpmin_x04;
  const recent24Value =
    activeTab === "tab1"
      ? flowStats?.flow_totilizer_x08
      : flowStats?.flow_totilizer_x04;
  const totalizerValue =
    activeTab === "tab1"
      ? flowStats?.current_totilizer_x08
      : flowStats?.current_totilizer_x04;
  const flowNoun = activeTab === "tab1" ? "Produced" : "Distributed";
  const formatValue = (value, unit = "") => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? `${parsed.toFixed(2)}${unit}` : "--";
  };

  const formatSignedValue = (value, unit = "") => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return "--";
    }
    return `${parsed >= 0 ? "+" : ""}${parsed.toFixed(2)}${unit}`;
  };

  const formatPercent = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return "--";
    }
    return `${parsed >= 0 ? "+" : ""}${parsed.toFixed(2)}%`;
  };

  const handlePeriodChange = (nextPeriod) => {
    setSelected(nextPeriod);
    setSelectedFilter && setSelectedFilter(nextPeriod);
    setPeriodOffset(0);
    setComparisonMode("previous");
    setCustomComparisonOffset(1);
    setComparisonEnabled(false);
    setZoomLevel(0);
    resetChartData();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelected("Day");
    setPeriodOffset(0);
    setComparisonMode("previous");
    setCustomComparisonOffset(1);
    setComparisonEnabled(false);
    setZoomLevel(0);
    setTab && setTab(tab);
    setSelectedFilter && setSelectedFilter("Day");
    resetChartData();
    resetFlowStats();
  };

  const setTimelineOffset = (nextOffset) => {
    const safeOffset = Math.max(0, Number(nextOffset) || 0);
    setPeriodOffset(safeOffset);
    resetChartData();
  };

  const handleComparisonModeChange = (nextMode) => {
    if (nextMode === "off") {
      setComparisonEnabled(false);
      setComparisonMode("previous");
      resetChartData();
      return;
    }

    setComparisonMode(nextMode);
    if (!comparisonEnabled) {
      setComparisonEnabled(true);
    }
    resetChartData();
  };

  const handleCustomComparisonChange = (value) => {
    const nextOffset = getOffsetFromPeriodInput(selected, value);
    const safeOffset =
      nextOffset === periodOffset ? Math.max(1, nextOffset + 1) : nextOffset;
    setCustomComparisonValue(value);
    setCustomComparisonOffset(safeOffset);
    setComparisonMode("custom");
    setComparisonEnabled(true);
    resetChartData();
  };

  return (
    <CardWrapper>
      <header className="dashboard-flow-header p-2 pb-2 py-3 md:p-6 md:pb-0 md:pt-2">
        <div className="dashboard-chart-title-row">
          <div>
            <div className="dashboard-page-title mb-2">Water Flow & Volume</div>
            <div className="dashboard-period-nav" aria-label="Selected period">
              <button
                type="button"
                className="dashboard-period-nav-btn"
                onClick={() => setTimelineOffset(periodOffset + 1)}
                aria-label={`Show previous ${selected.toLowerCase()}`}
              >
                <ChevronLeftIcon />
              </button>
              <span className="dashboard-chart-subtitle">
                {periodDisplayLabel}
              </span>
              <button
                type="button"
                className="dashboard-period-nav-btn"
                disabled={periodOffset === 0}
                onClick={() => setTimelineOffset(Math.max(0, periodOffset - 1))}
                aria-label={`Show next ${selected.toLowerCase()}`}
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="dashboard-chart-toolbar">
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            type="secondary"
          />

          <div className="dashboard-chart-actions">
            <div className="dashboard-period-segmented" aria-label="Period">
              {PERIOD_OPTIONS.map((period) => (
                <button
                  key={period}
                  type="button"
                  className={`dashboard-period-chip ${
                    selected === period ? "is-active" : ""
                  }`}
                  onClick={() => handlePeriodChange(period)}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="dashboard-chart-inline-nav" aria-label="Chart period navigation">
          <span className="dashboard-chart-inline-label">
            {periodDisplayLabel}
          </span>
          <div className="dashboard-chart-inline-actions">
            <button
              type="button"
              className="dashboard-chart-inline-nav-btn"
              onClick={() => setTimelineOffset(periodOffset + 1)}
              aria-label={`Show previous ${selected.toLowerCase()}`}
              title={`Previous ${selected.toLowerCase()}`}
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className="dashboard-chart-inline-nav-btn"
              disabled={periodOffset === 0}
              onClick={() => setTimelineOffset(Math.max(0, periodOffset - 1))}
              aria-label={`Show next ${selected.toLowerCase()}`}
              title={`Next ${selected.toLowerCase()}`}
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>

      </header>

      <div className="w-full p-2 pl-1 md:p-6 pb-0 md:pb-0">
        {activeFlow ? (
          <BarChart
            barData={activeFlow}
            selected={selected}
            refinedLabel={refinedLabel}
            zoomLevel={zoomLevel}
            onZoomChange={setZoomLevel}
            showZoomControls={false}
            series={activeSeries}
          />
        ) : (
          <GraphSkeleton />
        )}

        <div className="dashboard-flow-metrics" aria-label="Water flow totals">
          <div className="dashboard-flow-metric">
            <span>Current {flowNoun.toLowerCase()}</span>
            <strong>{formatValue(currentFlowValue)}</strong>
            <small>l/min</small>
          </div>
          <div className="dashboard-flow-metric is-primary">
            <span>Recent 24 hours</span>
            <strong>{formatValue(recent24Value)}</strong>
            <small>m3</small>
          </div>
          <div className="dashboard-flow-metric">
            <span>Total flow value</span>
            <strong>{formatValue(totalizerValue)}</strong>
            <small>m3</small>
          </div>
        </div>

        {activeComparison && (
          <div
            className={`dashboard-comparison-panel ${
              comparisonEnabled ? "has-comparison" : ""
            }`}
          >
            <div className="dashboard-comparison-item is-selected">
              <span>This period</span>
              <strong>
                {formatValue(activeComparison.currentTotalM3, " m3")}
              </strong>
              <small>{activeComparison.currentLabel}</small>
            </div>
            {comparisonEnabled && (
              <>
                <div className="dashboard-comparison-item is-compared">
                  <span>Compared</span>
                  <strong>
                    {formatValue(activeComparison.comparisonTotalM3, " m3")}
                  </strong>
                  <small>{activeComparison.comparisonLabel}</small>
                </div>
                <div className="dashboard-comparison-item is-delta">
                  <span>Difference</span>
                  <strong>
                    {formatSignedValue(activeComparison.deltaM3, " m3")} (
                    {formatPercent(activeComparison.deltaPercent)})
                  </strong>
                  <small>{activeTab === "tab1" ? "Produced" : "Distributed"}</small>
                </div>
              </>
            )}
          </div>
        )}

        <div className="dashboard-compare-control">
          <label className="dashboard-compare-select-row">
            <span>Compare with</span>
            <select
              className="dashboard-compare-select"
              value={comparisonEnabled ? comparisonMode : "off"}
              onChange={(event) => handleComparisonModeChange(event.target.value)}
            >
              {comparisonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {comparisonEnabled && comparisonMode === "custom" && (
            <input
              className="dashboard-compare-input"
              inputMode={selected === "Year" ? "numeric" : undefined}
              max={selected === "Year" ? undefined : selectedDateValue}
              placeholder={getPeriodInputPlaceholder(selected)}
              type={getPeriodInputType(selected)}
              value={customComparisonValue}
              onChange={(event) =>
                handleCustomComparisonChange(event.target.value)
              }
              aria-label={`Custom comparison ${getPeriodUnit(selected)}`}
            />
          )}
        </div>
      </div>
    </CardWrapper>
  );
}

export default Bar;
